export const ADMIN_SHELL_PAGES = [
  'home',
  'inbox',
  'team',
  'agents',
  'initiatives',
  'views',
  'sandbox',
  'branch-review',
  'view-editor',
  'build',
  'operations-map',
  'integrations',
  'mcp',
  'ontology-builder',
  'ontology-data',
  'ontology-action-runs',
  'simulations',
  'program-editor',
  'automations',
  'org-chart',
  'account',
  'whatsapp',
  'senders',
  'contact',
];

/**
 * Retired pages whose links still circulate in sent email and saved bookmarks.
 * The shell redirects them; they are never navigation targets.
 */
export const LEGACY_REDIRECT_PAGES = [
  'mission-control',
  'missions',
  'notifications',
  'approvals',
  'agent-orchestration',
  'agent-command',
  'agent-configuration',
  // The data foundation's own page; its card and findings are on Home.
  'setup',
  // Roles & Access (and its older alias) folded into the Org Chart.
  'roles-access',
  'workers',
];

export const APP_SHELL_ROUTE_PREFIX = '/app';
export const LEGACY_ADMIN_SHELL_ROUTE_PREFIX = '/admin';

const PHASE1_CLOUD_SQL_TENANT_IDS = new Set(['leon', 'cmdppl']);

/**
 * Leon and CMD keep only the cutover-critical shell surface while their data
 * plane is activated. Other tenants and local development retain the existing
 * route policy.
 */
export const isPhase1CloudSqlRuntime = () => {
  if (!import.meta.env.PROD || typeof window === 'undefined') return false;
  const tenantId = window.__TENANT_RUNTIME__?.tenant?.tenantId;
  return PHASE1_CLOUD_SQL_TENANT_IDS.has(String(tenantId || '').trim().toLowerCase());
};

/**
 * Phase 1 denylist is empty: Configure product-data paths are API-only.
 * Keep the set for a future fail-closed slug if a page regresses to PostgREST.
 */
export const PHASE1_DISABLED_PAGES = new Set();

const CONFIGURE_LANDING_PRIORITY = [
  'home',
  'build',
  'view-editor',
  'automations',
  'integrations',
];

/** Pages available to platform_tier=admin (domain Configure). */
export const DOMAIN_ADMIN_PAGES = new Set([
  'build',
  'operations-map',
  'ontology-action-runs',
  'view-editor',
  'program-editor',
  'automations',
  'initiatives',
  'sandbox',
]);

/** Everyone works with the agent team: these pages are open to every member. */
export const MEMBER_PAGES = new Set(['home', 'inbox', 'team', 'agents', 'account', 'whatsapp', 'senders', 'mcp', 'contact', 'views', 'branch-review']);

/** Pages that additionally require is_superadmin. */
export const SUPERADMIN_PAGES = new Set([
  'integrations',
  'ontology-builder',
  'ontology-data',
  'org-chart',
  // Operations rehearsal (ADR-0016): only Superadmins create, run, fork and read simulations.
  'simulations',
]);

export const getAdminShellPathForPage = (page) =>
  page === 'branch-review' ? '/app/build/review' : page ? `${APP_SHELL_ROUTE_PREFIX}/${page}` : APP_SHELL_ROUTE_PREFIX;

export const getAdminShellPageFromPath = (pathname = '') => {
  if (/^\/app\/build\/review(?:\/|$)/.test(pathname)) return 'branch-review';
  if (!pathname.startsWith(APP_SHELL_ROUTE_PREFIX)) return null;
  const page = pathname.slice(APP_SHELL_ROUTE_PREFIX.length).replace(/^\/+/, '').split('/')[0];
  return ADMIN_SHELL_PAGES.includes(page) ? page : null;
};

export const getLegacyPageFromPath = (pathname = '') => {
  if (!pathname.startsWith(APP_SHELL_ROUTE_PREFIX)) return null;
  const page = pathname.slice(APP_SHELL_ROUTE_PREFIX.length).replace(/^\/+/, '').split('/')[0];
  return LEGACY_REDIRECT_PAGES.includes(page) ? page : null;
};

export const getDefaultRouteForRole = (role) => {
  return role ? APP_SHELL_ROUTE_PREFIX : '/';
};

/** Workspace access controls configuration; operational names never elevate. */
export const resolvePlatformAccess = (access) => {
  if (!access) return { role: null, platformTier: null, isSuperadmin: false };
  if (typeof access === 'string') return { role: access, platformTier: 'worker', isSuperadmin: false };
  const role = access.role || access.roleSlug || null;
  if ('workspaceAccess' in access || 'workspace_access' in access) {
    const level = access.workspaceAccess ?? access.workspace_access;
    return { role, platformTier: ['configure', 'administrator'].includes(level) ? 'admin' : 'worker', isSuperadmin: level === 'administrator' };
  }
  const platformTier = (access.platformTier || access.platform_tier) === 'admin' ? 'admin' : 'worker';
  const isSuperadmin = platformTier === 'admin' && (access.isSuperadmin ?? access.is_superadmin) === true;
  return { role, platformTier, isSuperadmin };
};

export const getAccessibleApplicationModulesForRole = () => [];

/**
 * Configure is an admin-tier workspace. Org-chart role alone does not grant it.
 */
export const isPlatformConfigurator = (roleOrAccess) => {
  const { platformTier } = resolvePlatformAccess(roleOrAccess);
  return platformTier === 'admin';
};

export const canAccessAdminPage = (roleOrAccess, page) => {
  if (!roleOrAccess || !page) return false;
  if (isPhase1CloudSqlRuntime() && PHASE1_DISABLED_PAGES.has(page)) return false;
  if (MEMBER_PAGES.has(page)) return true;
  if (page === 'sandbox') return true;

  const { platformTier, isSuperadmin } = resolvePlatformAccess(roleOrAccess);
  if (platformTier !== 'admin') return false;
  if (SUPERADMIN_PAGES.has(page)) return isSuperadmin;
  if (DOMAIN_ADMIN_PAGES.has(page)) return true;
  // Unknown configure page: admin tier only if superadmin (safe default)
  return isSuperadmin;
};

export const getDefaultAdminPageForRole = (roleOrAccess) => {
  const { platformTier } = resolvePlatformAccess(roleOrAccess);
  if (platformTier !== 'admin') return 'home';
  for (const page of CONFIGURE_LANDING_PRIORITY) {
    if (canAccessAdminPage(roleOrAccess, page)) return page;
  }
  return 'views';
};
